import { useEffect, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { copy } from "../copy";
import Countdown from "../components/Countdown";
import Icon, { type IconName } from "../components/Icon";
import SiteFooter from "../components/SiteFooter";
import { primaryButton, secondaryButton } from "../contest/ui";
import { backMeHref, contestHref } from "../feed";
import { useProducer, type Producer as ProducerData, type ProducerContest } from "../producer/api";
import { useSession, useSessionReady } from "../session";
import CoSignPanel from "../producer/CoSignPanel";
import NotifyPanel from "../producer/NotifyPanel";
import { outcomeOf, useCoSignMarkets } from "../producer/cosign";
import { cosignCopy } from "../producer/cosignCopy";
import type { Market } from "../contest/api";

function Notice({ testId, icon, eyebrow, heading, body, action }: {
  testId: string;
  icon: IconName;
  eyebrow?: string;
  heading: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <div data-testid={testId} className="mt-16 max-w-xl rounded-2xl border border-ground-edge bg-ground-raised p-6 md:p-8">
      <Icon name={icon} className="h-6 w-6 text-chrome-dim" />
      {eyebrow && <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{eyebrow}</p>}
      <h1 className="mt-3 font-headline text-4xl uppercase leading-[0.95] text-chrome">{heading}</h1>
      <p className="mt-4 text-sm leading-relaxed text-chrome-dim">{body}</p>
      {action}
    </div>
  );
}

const LINKS: { key: keyof ProducerData["links"]; icon: IconName }[] = [
  { key: "instagram", icon: "instagram" },
  { key: "twitter", icon: "twitterX" },
  { key: "soundcloud", icon: "soundcloud" },
  { key: "website", icon: "global" },
];

function monthYear(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function Header({ producer }: { producer: ProducerData }) {
  const c = copy.producerPage;
  const since = monthYear(producer.member_since);
  const links = LINKS.filter((l) => producer.links[l.key]);
  return (
    <header className="mt-8 flex flex-col gap-6 border-b border-ground-edge pb-10 md:flex-row md:items-end">
      <span className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-gold/60 bg-ground-raised md:h-36 md:w-36">
        {producer.avatar_url ? (
          <img src={producer.avatar_url} alt={c.avatar(producer.username)} className="h-full w-full object-cover object-top" />
        ) : (
          <span aria-hidden className="font-headline text-5xl uppercase text-chrome-dim">
            {producer.username.charAt(0) || "?"}
          </span>
        )}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{c.eyebrow}</p>
        <h1 data-testid="producer-username" className="mt-2 break-words font-headline text-5xl uppercase leading-[0.9] tracking-[-0.02em] text-chrome md:text-7xl">
          @{producer.username}
        </h1>
        {since && <p className="mt-3 text-xs text-chrome-dim">{c.memberSince(since)}</p>}
        {producer.bio && (
          <p data-testid="producer-bio" className="mt-4 max-w-xl whitespace-pre-line text-sm leading-relaxed text-chrome">
            {producer.bio}
          </p>
        )}
        {links.length > 0 && (
          <ul className="mt-5 flex flex-wrap gap-2">
            {links.map((l) => (
              <li key={l.key}>
                <a
                  data-testid="producer-link"
                  href={producer.links[l.key]}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="bf-focusable inline-flex items-center gap-2 rounded-full border border-ground-edge bg-ground-raised px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome transition-colors hover:border-chrome-dim"
                >
                  <Icon name={l.icon} className="h-4 w-4" />
                  {c.links[l.key]}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </header>
  );
}

function Stats({ producer }: { producer: ProducerData }) {
  const c = copy.producerPage;
  const s = producer.stats;
  const tiles: { label: string; value: string; tone: string }[] = [
    ...(s.rank ? [{ label: c.rank, value: `#${s.rank}`, tone: "text-chrome" }] : []),
    { label: c.entered, value: s.contests_entered.toLocaleString("en-US"), tone: "text-chrome" },
    { label: c.won, value: s.contests_won.toLocaleString("en-US"), tone: "text-gold" },
    { label: c.winnings, value: s.total_winnings_display, tone: "text-money" },
  ];
  return (
    <section aria-label={c.stats} className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
      {tiles.map((t) => (
        <div key={t.label} data-testid="producer-stat" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
          <p className={`bf-tabular font-headline text-4xl leading-none ${t.tone}`}>{t.value}</p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{t.label}</p>
        </div>
      ))}
    </section>
  );
}

function LiveCard({ contest, username, market, onMarket }: {
  contest: ProducerContest;
  username: string;
  // This viewer's read of the contest's market, when it came back. It says
  // whether they may co-sign, which the anonymous producer payload cannot.
  market?: Market;
  onMarket: (market: Market) => void;
}) {
  const c = copy.producerPage;
  const open = contest.market?.status === "open";
  const outcome = market ? outcomeOf(market, username) : undefined;
  // The market read is the fresher of the two, so a co-sign placed here
  // moves the pot and the odds on the card it was placed from.
  const m =
    market && outcome
      ? {
          status: market.status,
          pool_display: market.pool_display,
          american_odds: outcome.american_odds,
          share_bps: outcome.share_bps,
          backer_count: outcome.backer_count,
        }
      : contest.market;
  const refusal =
    market?.status === "open" && market.viewer.reason === "entrant"
      ? cosignCopy.entrant
      : market?.status === "open" && market.viewer.reason === "unverified"
        ? cosignCopy.unverified
        : null;
  return (
    <li data-testid="producer-live-card" className="group flex flex-col overflow-hidden rounded-2xl border border-ground-edge bg-ground-raised">
      <a
        href={contestHref(contest.slug)}
        aria-label={copy.contests.open(contest.name)}
        className="bf-focusable relative block aspect-[16/9] w-full overflow-hidden"
      >
        {contest.cover_url ? (
          <img
            data-testid="producer-live-cover"
            src={contest.cover_url}
            alt={copy.contests.cover(contest.name)}
            loading="lazy"
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-ground bg-[radial-gradient(circle_at_30%_20%,rgba(247,201,72,0.18),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(43,217,107,0.14),transparent_50%)]">
            <Icon name="disc" className="h-12 w-12 text-chrome-dim/60" />
          </div>
        )}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ground-raised via-transparent to-transparent" />
        {contest.state === "judging" && (
          <span className="absolute right-3 top-3 rounded-full border border-gold/50 bg-ground/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-gold backdrop-blur-sm">
            {c.voting}
          </span>
        )}
      </a>
      <div className="flex flex-1 flex-col p-5 pt-3">
        <h3 className="font-headline text-2xl uppercase leading-[0.95] text-chrome">{contest.name}</h3>
        {contest.state === "open" && contest.ends_at && (
          <div className="mt-4">
            <Countdown endsAt={contest.ends_at} />
          </div>
        )}
        {m && (
          <div className="mt-5 flex items-end justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{c.odds}</p>
              <p data-testid="producer-odds" className="bf-tabular font-headline text-4xl leading-none text-money">
                {m.american_odds}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{c.pot}</p>
              <p className="bf-tabular text-lg font-bold text-chrome">{m.pool_display}</p>
              <p className="text-[11px] text-chrome-dim">{c.fans(m.backer_count)}</p>
            </div>
          </div>
        )}
        <div className="mt-auto pt-5">
          {market && outcome && market.status === "open" && market.viewer.can_back ? (
            <CoSignPanel contest={contest} username={username} market={market} outcome={outcome} onMarket={onMarket} />
          ) : refusal ? (
            <>
              <p data-testid="producer-cosign-refused" className="text-[11px] leading-relaxed text-chrome-dim">
                {refusal}
              </p>
              <a href={contestHref(contest.slug)} className={`${secondaryButton} mt-3 w-full`}>
                {c.viewContest}
              </a>
            </>
          ) : open ? (
            <a data-testid="producer-back" href={backMeHref(contest.slug, username)} className={`${primaryButton} w-full`}>
              {c.back} @{username}
            </a>
          ) : (
            <a href={contestHref(contest.slug)} className={`${secondaryButton} w-full`}>
              {c.viewContest}
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

// /p/:username. Go serves this path (the shell carries the producer's
// link-preview tags); the page reads GET /v1/web/producers/{username}.
export default function Producer() {
  const { username = "" } = useParams();
  const { state, retry } = useProducer(username);
  const c = copy.producerPage;
  const producer = state.status === "ready" ? state.producer : null;
  // Only a signed-in fan can co-sign, so only their page reads the markets.
  // Until the session read settles nothing is fetched, so a member is never
  // mistaken for a visitor.
  const sessionReady = useSessionReady();
  const signedIn = useSession().kind !== "visitor";
  const { markets, onMarket } = useCoSignMarkets(sessionReady && signedIn ? (producer?.live ?? []) : []);

  useEffect(() => {
    document.title = producer ? c.title(producer.username) : c.loadingTitle;
  }, [producer, c]);

  let body: ReactNode = null;
  if (state.status === "notfound") {
    body = (
      <Notice
        testId="producer-not-found"
        icon="searchEye"
        eyebrow={c.notFoundEyebrow}
        heading={c.notFound}
        body={c.notFoundSub}
        action={
          <Link to="/ranking" className={`${primaryButton} mt-6`}>
            {c.rankings}
          </Link>
        }
      />
    );
  } else if (state.status === "error") {
    body = (
      <Notice
        testId="producer-error"
        icon="errorWarning"
        heading={c.error}
        body={c.errorSub}
        action={
          <button type="button" onClick={retry} className={`${primaryButton} mt-6`}>
            {c.retry}
          </button>
        }
      />
    );
  } else if (producer) {
    body = (
      <>
        <Header producer={producer} />
        <Stats producer={producer} />
        {producer.live.length === 0 && (
          <section className="mt-12">
            <h2 className="font-headline text-3xl uppercase leading-none text-chrome">{c.inNow}</h2>
            <NotifyPanel username={producer.username} />
          </section>
        )}
        {producer.live.length > 0 && (
          <section data-testid="producer-live" className="mt-12">
            <h2 className="font-headline text-3xl uppercase leading-none text-chrome">{c.inNow}</h2>
            <p className="mt-2 text-sm text-chrome-dim">{c.inNowSub}</p>
            <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {producer.live.map((contest) => (
                <LiveCard
                  key={contest.slug}
                  contest={contest}
                  username={producer.username}
                  market={markets[contest.slug]}
                  onMarket={(market) => onMarket(contest.slug, market)}
                />
              ))}
            </ul>
          </section>
        )}
        {producer.wins.length > 0 && (
          <section data-testid="producer-wins" className="mt-12">
            <h2 className="font-headline text-3xl uppercase leading-none text-chrome">{c.wins}</h2>
            <ul className="mt-6 divide-y divide-ground-edge/60 rounded-2xl border border-ground-edge bg-ground-raised">
              {producer.wins.map((w, i) => (
                <li key={`${w.contest_slug ?? ""}-${w.at}-${i}`} data-testid="producer-win" className="flex items-center justify-between gap-4 px-5 py-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ground-edge bg-ground">
                      {w.cover_url ? (
                        <img data-testid="producer-win-thumb" src={w.cover_url} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
                      ) : (
                        <Icon name="trophy" className="h-5 w-5 text-gold" />
                      )}
                    </span>
                    {w.contest_slug ? (
                      <a href={contestHref(w.contest_slug)} className="truncate text-sm font-bold text-chrome hover:underline">
                        {w.contest_name}
                      </a>
                    ) : (
                      <span className="truncate text-sm font-bold text-chrome">{w.contest_name}</span>
                    )}
                    {w.placement ? <span className="shrink-0 text-[11px] uppercase text-chrome-dim">{c.place(w.placement)}</span> : null}
                  </div>
                  <span className="bf-tabular shrink-0 text-sm font-bold text-money">{w.amount_display}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1240px]">
          <Link
            to="/ranking"
            className="bf-focusable inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim transition-colors hover:text-chrome"
          >
            <Icon name="arrowLeft" className="h-4 w-4" />
            {copy.ranking.heading}
          </Link>
          {body}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
